import { forwardRef } from 'react';
import { default as BaseAlert } from '@mui/joy/Alert';
import XClose from '@unifyapps/icons/outline/XClose';
import clsx from 'clsx';
import { decode18NInterpolatedString } from '../../utils/decode18NInterpolatedString';
import { Typography } from '../Typography';
import { Box } from '../Box';
import { IconButton } from '../IconButton';
import { Button } from '../Button';
import type { AlertProps } from './types';
import { alertVariants, containerVariants, infoVariants } from './styles';

const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  const {
    className,
    direction = 'row',
    startDecoratorComponent: StartDecoratorComponent,
    startDecoratorNode,
    endDecoratorNode,
    color = 'default',
    title,
    titleColor = 'text-secondary',
    description,
    primaryAction,
    secondaryAction,
    onClose,
    bottomDecoratorNode,
    slotProps = {},
    ...rest
  } = props;

  const alertClassName = alertVariants({ className, color, direction });
  const containerClassName = containerVariants({ direction });
  const defaultInfoClassName = infoVariants({ direction });

  const startDecorator = StartDecoratorComponent ? (
    <StartDecoratorComponent height={38} width={38} />
  ) : (
    startDecoratorNode
  );

  const content = (
    <Box className={containerClassName}>
      <Box className={defaultInfoClassName} {...slotProps.info}>
        {title ? (
          <Typography color={titleColor} variant="text-sm" weight="semi-bold">
            {decode18NInterpolatedString(title)}
          </Typography>
        ) : null}
        {title && description && direction === 'row' ? ' ' : null}
        {description ? (
          <Typography
            className="break-anywhere"
            color="text-tertiary"
            variant="text-sm"
            weight="regular"
          >
            {description}
          </Typography>
        ) : null}
        {endDecoratorNode ? endDecoratorNode : null}
      </Box>
      {/* check if className needed after LinkButton updated */}
      {primaryAction || secondaryAction ? (
        <Box className="gap-x-lg flex flex-row items-center">
          {secondaryAction?.text ? (
            <Button
              color="neutral"
              component={secondaryAction.href ? 'a' : 'button'}
              href={secondaryAction.href}
              onClick={secondaryAction.onClick}
              size="md"
              target={secondaryAction.href ? '_blank' : undefined}
              variant="ghost"
            >
              {secondaryAction.text}
            </Button>
          ) : null}
          {primaryAction?.text ? (
            <Button
              color={primaryAction.color ?? 'brand'}
              component={primaryAction.href ? 'a' : 'button'}
              endDecoratorComponent={primaryAction.endDecoratorComponent}
              href={primaryAction.href}
              onClick={primaryAction.onClick}
              size={primaryAction.size ?? 'md'}
              target={primaryAction.href ? '_blank' : undefined}
              variant={primaryAction.variant ?? 'solid'}
            >
              {primaryAction.text}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <BaseAlert
      className={alertClassName}
      ref={ref}
      startDecorator={startDecorator}
      variant="soft"
      {...slotProps.root}
      {...rest}
    >
      <Box className="flex flex-1 flex-row">
        {bottomDecoratorNode ? (
          <Box className="flex flex-col gap-y-3">
            {content}
            {bottomDecoratorNode}
          </Box>
        ) : (
          content
        )}
        {onClose ? (
          <IconButton
            Icon={XClose}
            className={clsx('ms-auto', slotProps.closeButton?.className)}
            color="neutral"
            iconClassName={slotProps.closeButton?.iconClassName}
            onClick={onClose}
            size="md"
            variant="soft"
          />
        ) : null}
      </Box>
    </BaseAlert>
  );
});

Alert.displayName = 'Alert';

export default Alert;
